import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const PortalDashboard = () => {
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const response = await api.getPortalDashboard();
            if (response.success) {
                setStats(response.data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="animate-pulse space-y-8">
        <div className="h-32 bg-slate-200 rounded-3xl"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-64 bg-slate-200 rounded-3xl"></div>
            <div className="h-64 bg-slate-200 rounded-3xl"></div>
        </div>
    </div>;

    return (
        <div className="space-y-8 animate-in fade-in duration-700">
            {/* Balance Card */}
            <div className="bg-indigo-600 rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl shadow-indigo-200">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                <p className="text-indigo-100 text-sm font-bold uppercase tracking-widest mb-2">Account Balance</p>
                <div className="text-4xl font-black mb-1">₹{stats?.customer?.balance?.toLocaleString()}</div>
                <p className="text-indigo-100/80 text-sm font-medium">Pending amount on your account</p>
                
                <Link to="/portal/orders" className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-white text-indigo-600 rounded-xl font-bold text-sm hover:bg-indigo-50 transition-colors">
                    Make Payment
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                </Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Upcoming Deliveries */}
                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-black text-slate-900 tracking-tight">Upcoming Deliveries</h3>
                        <Link to="/portal/deliveries" className="text-indigo-600 font-bold text-sm hover:underline">View All</Link>
                    </div>
                    {stats?.upcoming_deliveries?.length > 0 ? (
                        <div className="space-y-4">
                            {stats.upcoming_deliveries.map((delivery: any) => (
                                <div key={delivery.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center text-indigo-600">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" /></svg>
                                        </div>
                                        <div>
                                            <p className="font-bold text-slate-900">{delivery.product}</p>
                                            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">{new Date(delivery.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</p>
                                        </div>
                                    </div>
                                    <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${delivery.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                        {delivery.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-slate-500 font-medium bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                            No upcoming deliveries
                        </div>
                    )}
                </div>

                {/* Recent Orders */}
                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-black text-slate-900 tracking-tight">Recent Orders</h3>
                        <Link to="/portal/orders" className="text-indigo-600 font-bold text-sm hover:underline">View History</Link>
                    </div>
                    {stats?.recent_orders?.length > 0 ? (
                        <div className="space-y-4">
                            {stats.recent_orders.map((order: any) => (
                                <Link to={`/portal/orders/${order.id}`} key={order.id} className="block group">
                                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100 group-hover:border-indigo-200 group-hover:bg-indigo-50/50 transition-all">
                                        <div>
                                            <p className="font-bold text-slate-900 leading-none mb-1">#{order.number}</p>
                                            <p className="text-xs text-slate-500 font-medium tracking-tight">₹{order.total.toLocaleString()} • {new Date(order.date).toLocaleDateString()}</p>
                                        </div>
                                        <svg className="w-5 h-5 text-slate-300 group-hover:text-indigo-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-slate-500 font-medium bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                            No recent orders
                        </div>
                    )}
                </div>

                {/* Recent Invoices */}
                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-black text-slate-900 tracking-tight">Recent Invoices</h3>
                        <Link to="/portal/invoices" className="text-indigo-600 font-bold text-sm hover:underline">View All</Link>
                    </div>
                    {stats?.recent_invoices?.length > 0 ? (
                        <div className="space-y-4">
                            {stats.recent_invoices.map((invoice: any) => (
                                <Link to={`/portal/invoices/${invoice.id}`} key={invoice.id} className="block group">
                                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100 group-hover:border-indigo-200 group-hover:bg-indigo-50/50 transition-all">
                                        <div>
                                            <p className="font-bold text-slate-900 leading-none mb-1 uppercase">{invoice.number}</p>
                                            <p className="text-xs text-slate-500 font-medium tracking-tight">₹{invoice.total.toLocaleString()} • {new Date(invoice.date).toLocaleDateString()}</p>
                                        </div>
                                        <div className="text-right flex items-center gap-3">
                                            {invoice.balance > 0 && (
                                                <span className="text-[10px] font-black text-rose-500 uppercase tracking-tighter">₹{invoice.balance.toLocaleString()} Due</span>
                                            )}
                                            <svg className="w-5 h-5 text-slate-300 group-hover:text-indigo-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-slate-500 font-medium bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                            No recent invoices
                        </div>
                    )}
                </div>
            </div>

            {/* Quick Actions / Store link */}
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div>
                        <h3 className="text-xl font-black text-slate-900 tracking-tight">Visit our Store</h3>
                        <p className="text-slate-500 font-medium mt-1">Browse our latest products and place new orders.</p>
                    </div>
                    <Link to="/store" className="w-full md:w-auto px-8 py-4 bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700 transition-all text-center shadow-lg shadow-indigo-100">
                        Shop Now
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default PortalDashboard;
